import { getValidAccessToken } from '../../../lib/supabase';
import type {
  AdminTicketDetail,
  AdminTicketListResponse,
} from '../../../domain/superadmin/types';
import type { TicketStatus } from '../../../domain/support/guards';
import { SuperadminApiError } from './errors';

type TicketListParams = {
  page?: number;
  pageSize?: number;
  q?: string;
  status?: string;
};

async function adminTicketFetch<T>(
  path: string,
  params?: TicketListParams & { id?: string }
): Promise<T> {
  const accessToken = await getValidAccessToken();
  const url = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${path}`);
  if (params?.id) url.searchParams.set('id', params.id);
  if (params?.page) url.searchParams.set('page', String(params.page));
  if (params?.pageSize) url.searchParams.set('pageSize', String(params.pageSize));
  if (params?.q) url.searchParams.set('q', params.q);
  if (params?.status) url.searchParams.set('status', params.status);

  let response: Response;
  try {
    response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
    });
  } catch {
    throw new SuperadminApiError(
      'Não foi possível conectar à API admin. Verifique a rede ou se as Edge Functions estão deployadas.',
      0,
      'NETWORK'
    );
  }

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new SuperadminApiError(
      err.error || err.message || 'Falha na API admin',
      response.status,
      err.code
    );
  }

  return response.json();
}

async function adminTicketJsonRequest<T>(
  method: 'PATCH' | 'POST',
  path: string,
  body: Record<string, unknown>
): Promise<T> {
  const accessToken = await getValidAccessToken();
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${path}`;
  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new SuperadminApiError(
      'Não foi possível conectar à API admin. Verifique a rede ou se as Edge Functions estão deployadas.',
      0,
      'NETWORK'
    );
  }

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new SuperadminApiError(
      err.error || err.message || 'Falha na API admin',
      response.status,
      err.code
    );
  }

  return response.json();
}

export function fetchAdminTickets(
  params: TicketListParams = {}
): Promise<AdminTicketListResponse> {
  return adminTicketFetch('admin-list-tickets', params);
}

export function fetchAdminTicket(id: string): Promise<AdminTicketDetail> {
  return adminTicketFetch('admin-get-ticket', { id });
}

export function updateAdminTicket(id: string, status: TicketStatus): Promise<AdminTicketDetail> {
  return adminTicketJsonRequest('PATCH', 'admin-update-ticket', { id, status });
}

export function replyAdminTicket(ticketId: string, body: string): Promise<AdminTicketDetail> {
  return adminTicketJsonRequest('POST', 'admin-reply-ticket', { ticketId, body });
}
